'use client';

import { Play, MonitorSmartphone, ShieldCheck } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import Image from 'next/image';

export function DemoWorkspaceSection() {
  const videoUrl = process.env.NEXT_PUBLIC_DEMO_VIDEO_URL;
  const hasVideo = Boolean(videoUrl);

  const handleDemoClick = () => {
      analytics.ctaClick('demo_workspace_open', 'landing', { source: 'demo-section', cta_label: 'Launch Demo Visual' });
      window.location.href = '/demo-workspace';
  };

  return (
    <section className="py-16 bg-muted/30" id="demo-workspace">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold border border-primary/20">
              <MonitorSmartphone className="w-3.5 h-3.5" />
              Live Demo · No Signup Required
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Experience the platform <span className="text-primary">instantly</span>.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Explore a fully seeded workspace. See how instruments are tracked, view compliance dashboards, and generate an audit pack in real-time.
            </p>
            
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <div className="mt-1 w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-3 h-3 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Pre-loaded Data</p>
                  <p className="text-xs text-muted-foreground">Interact with realistic instruments and certificates.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-1 w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-3 h-3 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Full Audit Pack Generation</p>
                  <p className="text-xs text-muted-foreground">Generate and download the actual PDF/ZIP evidence pack.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Visual Side */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-3xl blur-2xl -z-10 opacity-70" />
            <div className="bg-background border rounded-2xl shadow-xl overflow-hidden cursor-pointer group" onClick={handleDemoClick}>
              {hasVideo ? (
                <div className="relative w-full aspect-video">
                  <iframe
                    title="Demo workspace video"
                    src={videoUrl}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              ) : (
                // Interactive Screenshot Visual
                <div className="w-full bg-muted/10 relative">
                   {/* Screenshot Image - using intrinsic sizing to prevent cropping */}
                   <Image 
                      src="/images/demo-workspace.png"
                      alt="Calibration Vault Instrument Register Demo"
                      width={1920}
                      height={1200}
                      className="w-full h-auto block opacity-90 group-hover:opacity-100 group-hover:scale-[1.01] transition-all duration-700"
                   />
                   
                   {/* Overlay Gradient */}
                   <div className="absolute inset-0 bg-background/10 group-hover:bg-transparent transition-colors" />

                   {/* Central Play Button Overlay */}
                   <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-background/80 backdrop-blur-md pl-6 pr-8 py-4 rounded-full border border-primary/20 shadow-2xl text-primary font-bold flex items-center gap-4 group-hover:scale-110 group-hover:shadow-primary/20 transition-all duration-300 hover:bg-background/95">
                         <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-inner shrink-0">
                            <Play className="w-5 h-5 fill-current ml-1" />
                         </div>
                         <div className="flex flex-col text-left whitespace-nowrap">
                            <span className="text-sm leading-tight text-foreground">Interactive Demo</span>
                            <span className="text-[10px] text-muted-foreground font-normal uppercase tracking-wider">Click to Launch</span>
                         </div>
                      </div>
                   </div>
                </div>
              )}
              <div className="p-3 bg-muted/50 border-t text-[10px] font-medium text-muted-foreground text-center uppercase tracking-widest">
                Interactive Environment • Read-Only Mode
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
